import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ShareButton from "@/components/feature/ShareButton";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { specialList } from "@/mocks/special";

export default function SpecialDetailPage() {
  const { id } = useParams();
  const special = specialList.find((item) => item.id === id) ?? specialList[0];

  useDocumentMeta({
    title: `${special.title} - 张柏芝特别专题`,
    description: `${special.intro} 专题包含 ${special.sections.length} 个章节、共 ${special.articleCount} 篇相关内容。`,
    keywords: `张柏芝,${special.tag},${special.kicker},张柏芝专题,明星深度报道`,
  });

  const others = specialList.filter((item) => item.id !== special.id);

  return (
    <SiteLayout>
      <section className="relative w-full overflow-hidden bg-foreground-950">
        <img
          src={special.cover}
          alt={`${special.title} - 张柏芝专题封面大图`}
          title={`${special.title} - 张柏芝专题`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-85"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/55 to-foreground-950/25"></div>
        <div className="relative w-full px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-10 lg:px-12">
          <div className="flex items-center gap-2 text-[11px] text-background-300">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/special" className="transition-colors hover:text-background-50">
              特别专题
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-background-100">{special.tag}</span>
          </div>

          <div className="mt-8 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-background-50">{special.kicker}</span>
              <span className="rounded-full border border-background-50/35 px-3 py-1 text-background-100">
                {special.period}
              </span>
              <span className="rounded-full border border-background-50/35 px-3 py-1 text-background-100">
                {special.articleCount} 篇内容
              </span>
            </div>
            <h1 className="mt-4 font-heading text-[26px] font-semibold leading-snug text-background-50 md:text-[40px] md:leading-tight">
              {special.title}
            </h1>
            <p className="mt-4 text-sm leading-7 text-background-200 md:text-[15px]">{special.intro}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ShareButton title={special.title} className="border-background-50/35 text-background-100 hover:border-background-50/70 hover:text-background-50" />
              <Link
                to="/special"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-3.5 py-2 text-xs text-background-100 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-list-check" aria-hidden="true"></i>
                全部专题
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-[1fr_320px] lg:px-12">
        <div className="min-w-0">
          <Breadcrumb items={[{ label: "特别专题", to: "/special" }, { label: special.tag }]} />
          <div className="mt-6 flex flex-col gap-8">
            {special.sections.map((section, index) => (
              <section key={section.id} aria-label={section.name}>
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-xs text-background-50">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h2 className="font-heading text-lg font-semibold text-foreground-950">{section.name}</h2>
                    <p className="mt-0.5 text-[11px] text-foreground-500">{section.note}</p>
                  </div>
                </div>
                <ul className="mt-4 flex flex-col gap-2 pl-11">
                  {section.items.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        className="group flex items-start justify-between gap-4 rounded-md border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300 hover:bg-primary-50/60"
                      >
                        <span className="min-w-0">
                          <span className="block text-[13px] leading-6 text-foreground-800 transition-colors group-hover:text-primary-700">
                            {item.label}
                          </span>
                          <span className="mt-1 block text-[11px] text-foreground-500">{item.meta}</span>
                        </span>
                        <i className="ri-arrow-right-up-line mt-1 text-foreground-400 transition-colors group-hover:text-primary-600" aria-hidden="true"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-10 rounded-lg bg-background-100 p-5">
            <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
              <i className="ri-lightbulb-line text-primary-600" aria-hidden="true"></i>
              推荐理由
            </h2>
            <p className="mt-2 text-[13px] leading-7 text-foreground-700">
              这个专题适合按顺序阅读：它不追求时效，而是把零散的报道按线索重新编排，让一个作品或一段经历可以被完整地看见。
              如果你只想快速了解近况，建议先看「现在的部分」，再回到前面补全上下文。
            </p>
          </div>
        </div>

        <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">专题信息</h2>
            <ul className="mt-3 space-y-3 text-xs">
              <li className="flex items-center justify-between border-b border-background-100 pb-2.5">
                <span className="text-foreground-600">专题类型</span>
                <span className="text-foreground-900">{special.tag}</span>
              </li>
              <li className="flex items-center justify-between border-b border-background-100 pb-2.5">
                <span className="text-foreground-600">策划周期</span>
                <span className="text-foreground-900">{special.period}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-foreground-600">章节数</span>
                <span className="text-foreground-900">{special.sections.length} 个</span>
              </li>
            </ul>
          </div>

          <div className="rounded-lg bg-secondary-100 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">切换其它专题</h2>
            <ul className="mt-3 space-y-2">
              {others.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/special/${item.id}`}
                    className="group flex items-center gap-3 rounded-md bg-background-50 p-2.5 transition-colors hover:bg-primary-50"
                  >
                    <span className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img src={item.cover} alt={`${item.title} 封面缩略图`} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-xs leading-5 text-foreground-800 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-[10px] text-foreground-500">{item.tag}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}